import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, IconButton, Input, CircularProgress, Card, CardContent } from '@mui/joy';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import Header from './Header';
import { LinearProgress, Modal } from '@mui/material';
import { endpoint } from '../../resource/Constant';
const AllFiles = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [defaultFiles, setDefaultFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [training, setTraining] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchDefaultFiles();
  }, []);

  const fetchDefaultFiles = async () => {
    try {
      const response = await fetch(endpoint + '/api/aichatbot/default_files');
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Failed to fetch default files.');
      setDefaultFiles(result.files.map((file, index) => ({ id: `default-${index}`, name: file })));
    } catch (err) {
      console.error(err);
    }
  };

  const handleUseDefaultFile = async (fileName) => {
    setTraining(true); // Open the modal
    setError('');
    setSuccess('');
    setProgress(0);

    try {
      const fileResponse = await fetch(endpoint + `/api/aichatbot/default_files/${fileName}`);
      if (!fileResponse.ok) throw new Error('Failed to fetch default file.');

      const fileBlob = await fileResponse.blob();
      const file = new File([fileBlob], fileName, { type: fileBlob.type });

      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(endpoint + '/api/aichatbot/uploadfile', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: formData
      });

      if (!uploadResponse.ok) throw new Error('File upload failed.');

      // Simulate progress updates
      let progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval); // Clear interval when progress reaches 100%
            setTraining(false); // Close the modal
            setSuccess(`"${fileName}" uploaded successfully!`);
            setTimeout(() => setSuccess(''), 4000);
            navigate('/chatbotbase/chatbot');
            // Clear success message after 3 seconds
            return 100;
          }
          return prev + 1;
        });
      }, 100);

      setUploadedFiles([...uploadedFiles, { id: Date.now(), name: fileName }]);
      // navigate('/chatbotbase/chatbot');
    } catch (err) {
      setError(err.message);
      setTraining(false); // Close the modal on error
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(endpoint + '/api/aichatbot/uploadfile', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: formData
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'File upload failed.');

      setUploadedFiles([...uploadedFiles, { id: Date.now(), name: file.name }]);
      setSuccess(`"${file.name}" uploaded successfully!`); // Success message
      setTimeout(() => setSuccess(''), 3000); // Hide after 3 sec
      navigate('/chatbotbase/chatbot');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== id));
  };

  return (
    <>
      <Header />
      {training == false && (
        <Box className="upload-container">
          <Box
            sx={{
              maxWidth: '600px',
              width: '100%',
              p: 4,
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              display: 'flex'
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography level="h2" sx={{ mb: [0.6], textAlign: 'center' }}>
                Upload Knowledge Base
              </Typography>
              <Typography level="h6" sx={{ mb: 3, textAlign: 'center', fontWeight: 150 }}>
                Upload PDF files to train your AI assistant with your data
              </Typography>
              <Box sx={{ p: 4, border: '2px dashed #aaa', borderRadius: '12px', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
                <Typography level="body-md">Drag & drop your files here</Typography>
                <Typography level="body-sm" sx={{ color: 'gray' }}>
                  or click to browse files
                </Typography>
                <Typography level="body-xs" sx={{ color: 'gray', mt: 1 }}>
                  Supported formats: PDF (Max size: 10MB)
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button component="label" variant="soft" color="primary" startDecorator={<UploadIcon />} disabled={loading}>
                    {loading ? <CircularProgress size="sm" /> : 'Upload File'}
                    <Input type="file" sx={{ display: 'none' }} onChange={handleFileUpload} />
                  </Button>
                </Box>
              </Box>

              <Typography level="h4" sx={{ mt: 4, textAlign: 'center' }}>
                Or use a sample PDF
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                {defaultFiles.map((file) => (
                  <Card key={file.id} variant="outlined" sx={{ width: '180px', textAlign: 'center', p: 2, wordWrap: 'break-word' }}>
                    <CardContent>
                      <Typography level="body-md" sx={{ wordBreak: 'break-word', fontSize: '14px' }}>
                        {file.name}
                      </Typography>
                      <Button size="sm" sx={{ mt: 1 }} onClick={() => handleUseDefaultFile(file.name)}>
                        Use this
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {success && (
                <Typography level="body-sm" sx={{ color: 'green', mt: 2, textAlign: 'center' }}>
                  {success}
                </Typography>
              )}
              {error && (
                <Typography level="body-sm" sx={{ color: 'red', mt: 2, textAlign: 'center' }}>
                  {error}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Modal open={training} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box
          sx={{
            backgroundColor: 'white',
            p: 4,
            borderRadius: '12px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            width: '400px'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Training Your AI Assistant
          </Typography>
          <Typography variant="body2" sx={{ color: 'gray', mb: 2 }}>
            Please wait while we prepare your custom AI assistant
          </Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, mb: 2 }} />
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {progress}%
          </Typography>
          <Box sx={{ textAlign: 'left', mt: 3 }}>
            <Typography variant="body2" sx={{ color: progress >= 25 ? 'black' : 'gray', fontWeight: 'bold', mb: 1 }}>
              {progress >= 25 ? '✅' : '1.'} Analyzing data...
            </Typography>
            <Typography variant="body2" sx={{ color: progress >= 50 ? 'black' : 'gray', fontWeight: 'bold', mb: 1 }}>
              {progress >= 50 ? '✅' : '2.'} Processing documents...
            </Typography>
            <Typography variant="body2" sx={{ color: progress >= 75 ? 'black' : 'gray', fontWeight: 'bold', mb: 1 }}>
              {progress >= 75 ? '✅' : '3.'} Training AI model...
            </Typography>
            <Typography variant="body2" sx={{ color: progress === 100 ? 'black' : 'gray', fontWeight: 'bold' }}>
              {progress === 100 ? '✅' : '4.'} Finalizing setup...
            </Typography>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AllFiles;
